import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Check,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  Settings,
  AlertCircle,
  Edit,
  Save,
  CheckSquare,
  Filter,
  Pencil,
  UserPlus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from "@/components/ui/textarea";
import { Command, CommandInput } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/datatablepagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { SingleUserCombobox } from '@/components/ui/SingleUserCombobox';
import { FrameworkCombobox, FrameworksProvider } from '@/components/ui/FrameworkCombobox';
import { acceptSuggestedTaskItem, rejectSuggestedTaskItem } from '@/services/aiSuggestionsService';
import { DataTable } from '@/components/ui/datatable';
import { ColumnDef } from '@tanstack/react-table';
import { getFrameworkFromIsoClauses, extractControlIds, getFrameworkNameFromClause, formatIsoClausesAsPills } from '@/utils/framework-utils';
import {
  TableItemSource,
  NormalizedTableItem,
  UnifiedTableCellData,
  UpdateCellType
} from './types';

// This is the one we use rn. 
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░██████████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░████░░░░░░░░░░████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░██░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░████░░░░░░░░▒▒██████░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░██░░██░░░░▒▒▒▒░░▒▒██░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░██████░░░░▒▒▒▒▒▒▒▒██░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░▒▒▒▒▒▒▒▒████░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░████░░░░░░░░░░░░░░░░░░░░████████░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░██████░░░░░░████░░░░░░░░░░░░░░████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░██░░░░██████░░░░██░░░░░░████████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░██░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░██░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░██░░░░██░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░████░░░░░░░░░░████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██░░░░░░░░░░░░░░██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██▒▒██████▒▒██████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██▒▒▒▒▒▒██▒▒▒▒▒▒▒▒██░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░██████░░██████████░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░
// ░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░░


// Form state type for task fields
interface ItemFormState {
  framework?: string;
  assignee?: string;
  evidenceType?: string;
  priority?: 'low' | 'medium' | 'high' | string;
  checkInterval?: string;
  reminderDays?: string;
  meetingRequired?: boolean;
  status?: string;
}

// Default form values
const defaultFormState: ItemFormState = {
  framework: '',
  assignee: '',
  evidenceType: 'Upload Evidence', // Default to Upload Evidence
  checkInterval: '365', // Default to 365 days
  reminderDays: '7', // Default to 7 days
  meetingRequired: true, // Default to Yes
  status: 'pending'
};

// Props for the component
interface UnifiedTableCellProps {
  cellData: UnifiedTableCellData;
  onUpdate?: () => void;
  onAcceptItem?: (sourceId: string, item: NormalizedTableItem) => void;
  onRejectItem?: (sourceId: string, item: NormalizedTableItem) => void;
  onAcceptSOAControl?: (controlId: string, assignee: string, status: string) => void;
}

// Helper function to convert any value to an array
const toArray = (value: any): any[] => (Array.isArray(value) ? value : value ? [value] : []);

// Format confidence value (can be 0..1 or 0..100)
const formatConfidence = (confidence?: number): string => {
  if (confidence === undefined || confidence === null || isNaN(confidence)) return '—';
  const pct = confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);
  return `${pct}%`;
};

// Main component
const UnifiedTableCell = ({
  cellData,
  onUpdate,
  onAcceptItem,
  onRejectItem,
  onAcceptSOAControl
}: UnifiedTableCellProps): React.ReactNode => {
  // Extract items from cell data
  const items = cellData.data.items || [];

  // Item descriptions being edited
  const [itemDescriptions, setItemDescriptions] = useState<Record<string, string>>({});

  // Global form state (prefills)
  const [globalFormState, setGlobalFormState] = useState<ItemFormState>(defaultFormState);

  // Individual item form states (overrides)
  const [itemFormStates, setItemFormStates] = useState<Record<string, ItemFormState>>({});
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);

  // State for tracking which items are being edited
  const [editingItemIds, setEditingItemIds] = useState<Set<string>>(new Set());
  const [processingItems, setProcessingItems] = useState<Set<string>>(new Set());
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(new Set());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter state
  type FilterOption = 'all' | 'ai' | 'soa';
  const [activeFilter, setActiveFilter] = useState<FilterOption>('ai');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState<keyof NormalizedTableItem>('description');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Toggle edit mode for an item
  const toggleEditItem = useCallback((itemId: string) => {
    setEditingItemIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Handle form field changes
  const handleItemFormChange = useCallback((itemId: string, field: keyof ItemFormState, value: any) => {
    setItemFormStates(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: value
      }
    }));
  }, []);

  // Handle saving an item
  const handleSaveItem = async (item: NormalizedTableItem) => {
    try {
      setProcessingItems(prev => new Set(prev).add(item.id));
      // Save logic will differ based on item type
      if (item.itemType === TableItemSource.AI_SUGGESTION) {
        // AI suggestion save logic
        console.log('Saving AI suggestion', item);
      } else if (item.itemType === TableItemSource.SOA_CONTROL) {
        // SOA control save logic
        console.log('Saving SOA control', item);
      }

      setStatusMessage({ type: 'success', message: 'Item saved successfully' });
      setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
      toggleEditItem(item.id);
    } catch (error) {
      console.error('Error saving item:', error);
      setStatusMessage({ type: 'error', message: 'Failed to save item' });
      setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  // Handle accepting an item
  const handleAcceptItem = async (item: NormalizedTableItem) => {
    try {
      setProcessingItems(prev => new Set(prev).add(item.id));

      if (item.itemType === TableItemSource.AI_SUGGESTION) {
        // Get item form state (if any) or use defaults
        const itemForm = itemFormStates[item.id] || globalFormState;

        // Create an enriched item with form data
        const enrichedItem = {
          ...item,
          framework: itemForm.framework,
          assignee: itemForm.assignee,
          evidenceType: itemForm.evidenceType,
          checkInterval: parseInt(itemForm.checkInterval || '365', 10),
          reminderDays: parseInt(itemForm.reminderDays || '7', 10),
          meetingRequired: itemForm.meetingRequired !== undefined ? itemForm.meetingRequired : true,
          status: itemForm.status || 'pending',
          description: itemDescriptions[item.id] || item.description || ''
        };

        if (onAcceptItem) {
          console.log('Accepting AI suggestion item:', enrichedItem);
          
          // Use the enriched item data with all form values
          await onAcceptItem(item.sourceId, enrichedItem);
        }
      } else if (item.itemType === TableItemSource.SOA_CONTROL) {
        // Handle SOA Control acceptance
        const itemForm = itemFormStates[item.id] || globalFormState;
        if (onAcceptSOAControl) {
          await onAcceptSOAControl(
            item.originalId,
            itemForm.assignee || '',
            itemForm.status || 'Planned'
          );
        }
      }

      setStatusMessage({ type: 'success', message: 'Item accepted successfully' });
      setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
    } catch (error) {
      console.error('❌ Error accepting item:', error);
      setStatusMessage({ type: 'error', message: 'Failed to accept item' });
      setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
    }
  };

  // Handle rejecting an item (only for AI suggestions)
  const handleRejectItem = async (itemId: string) => {
    try {
      setProcessingItems(prev => new Set(prev).add(itemId));

      // Find the item being rejected
      const item = items.find(t => t.id === itemId);
      if (item && item.itemType === TableItemSource.AI_SUGGESTION) {
        // Call the parent's reject handler
        if (onRejectItem) {
          await onRejectItem(item.sourceId, item);
        } else {
          // Fallback to direct service call if no parent handler
          await rejectSuggestedTaskItem(item.sourceId, item.originalId);
        }
        setStatusMessage({ type: 'success', message: 'Item rejected successfully' });
        setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
        
        // Remove the item from selected items if it's selected
        setSelectedItemIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        
        // Remove the item from editing mode if it's being edited
        setEditingItemIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
        
        // Update the item form states to remove the rejected item
        setItemFormStates(prev => {
          const newStates = { ...prev };
          delete newStates[itemId];
          return newStates;
        });
        
        // Update the item descriptions to remove the rejected item
        setItemDescriptions(prev => {
          const newDescriptions = { ...prev };
          delete newDescriptions[itemId];
          return newDescriptions;
        });
      } else {
        // Not applicable for SOA controls
        setStatusMessage({ type: 'info', message: 'Reject not applicable for this item type' });
        setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
      }
    } catch (error) {
      console.error('❌ Error rejecting item:', error);
      setStatusMessage({ type: 'error', message: 'Failed to reject item' });
      setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
    } finally {
      setProcessingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  // Handle description change
  const handleDescriptionChange = useCallback((itemId: string, value: string) => {
    setItemDescriptions(prev => ({
      ...prev,
      [itemId]: value
    }));
  }, []);

  // Column definitions for DataTable
  const columns = useMemo<ColumnDef<NormalizedTableItem>[]>(
    () => [
      {
        id: 'description',
        header: 'Update',
        meta: {
          headerClassName: 'pl-0 pr-2 min-w-[400px] md:min-w-[500px] w-auto',
          cellClassName: 'pl-0 pr-4 min-w-[400px] md:min-w-[500px] whitespace-normal',
          disableDefaultPadding: true
        },
        accessorFn: (row) => itemDescriptions[row.id] || row.description || '',
        cell: ({ row }) => {
          const item = row.original;
          const isEditing = editingItemIds.has(item.id);
          const [editingDescription, setEditingDescription] = useState('');

          // Start editing when entering edit mode
          useEffect(() => {
            if (isEditing) {
              setEditingDescription(itemDescriptions[item.id] || item.description || '');
            }
          }, [isEditing, item.id, item.description, itemDescriptions]);

          const handleSave = () => {
            if (editingDescription.trim()) {
              handleDescriptionChange(item.id, editingDescription);
              toggleEditItem(item.id);
            }
          };

          const handleCancel = () => {
            toggleEditItem(item.id);
          };

          return (
            <div className="space-y-1">
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Input
                            value={editingDescription}
                            onChange={(e) => setEditingDescription(e.target.value)}
                            placeholder="Enter description"
                            className="flex-1"
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-600 hover:bg-green-50"
                            onClick={handleSave}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:bg-red-50"
                            onClick={handleCancel}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="font-medium cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors group relative"
                        onClick={() => toggleEditItem(item.id)}
                        title={itemDescriptions[item.id] || item.description || '(No description)'}
                      >
                        {itemDescriptions[item.id] || item.description || '(No description)'}
                      </div>
                    )}
                    {item.rationale && (
                      <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {item.rationale}
                      </div>
                    )}
                    {item.implementationDetails && (
                      <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {item.implementationDetails}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Badges section - show different badges based on item type */}
              <div className="mt-1 flex flex-wrap gap-1 items-center">
                {item.itemType === TableItemSource.AI_SUGGESTION && item.confidence !== undefined && (
                  <Badge
                    variant="outline"
                    className="text-xs font-normal gap-1 h-5"
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${(item.confidence > 0.8 || item.confidence > 80) ? 'bg-green-500' :
                      (item.confidence > 0.5 || item.confidence > 50) ? 'bg-yellow-500' : 'bg-red-500'
                      }`} />
                    {formatConfidence(item.confidence)}
                  </Badge>
                )}
                {item.itemType === TableItemSource.AI_SUGGESTION && item.isoClauses && item.isoClauses.length > 0 && (
                  <>
                    {/* Compact framework/control pills */}
                    {formatIsoClausesAsPills(item.isoClauses).map((pill, i) => (
                      <Badge key={i} variant="outline" className="text-xs font-normal h-5">
                        {pill}
                      </Badge>
                    ))}
                  </>
                )}
                {item.itemType === TableItemSource.SOA_CONTROL && item.status && (
                  <Badge variant="outline" className="text-xs font-normal h-5">
                    Status: {item.status}
                  </Badge>
                )}
                {item.itemType === TableItemSource.SOA_CONTROL && item.controlNumber && (
                  <Badge variant="outline" className="text-xs font-normal h-5">
                    Control: {item.controlNumber}
                  </Badge>
                )}
              </div>
            </div>
          );
        },
      },
      {
        id: 'source',
        header: 'Source',
        meta: { headerClassName: 'w-[180px]', cellClassName: 'w-[180px] max-w-[180px] truncate' },
        accessorFn: (row) => {
          if (row.itemType === TableItemSource.AI_SUGGESTION) {
            return row.documentName || '';
          } else {
            return 'Control';
          }
        },
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="text-sm line-clamp-3">
              {item.itemType === TableItemSource.AI_SUGGESTION ?
                (item.documentName || '—') :
                `Control ${item.controlNumber || item.originalId}`
              }
            </div>
          );
        },
      },
      {
        id: 'assignee',
        header: 'Assignee',
        meta: { headerClassName: 'w-[180px]', cellClassName: 'w-[180px]' },
        accessorFn: (row) => itemFormStates[row.id]?.assignee || '',
        cell: ({ row }) => {
          const item = row.original;
          return (
            <div className="w-full">
              <SingleUserCombobox
                selected={itemFormStates[item.id]?.assignee || ''}
                setSelected={(value) => handleItemFormChange(item.id, 'assignee', value)}
                placeholder="Select assignee"
                className="w-full"
              />
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        meta: { headerClassName: 'w-[180px]', cellClassName: 'w-[180px]' },
        cell: ({ row }) => {
          const item = row.original;
          const isProcessing = processingItems.has(item.id);
          const isEditing = editingItemIds.has(item.id);

          return (
            <div className="flex items-center gap-2 justify-end">
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                    onClick={() => handleSaveItem(item)}
                    disabled={isProcessing}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                    onClick={() => toggleEditItem(item.id)}
                    disabled={isProcessing}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                    onClick={() => handleAcceptItem(item)}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                    ) : (
                      <Check className="h-4 w-4" />
                    )}
                  </Button>
                  {/* Only show reject button for AI suggestions */}
                  {item.itemType === TableItemSource.AI_SUGGESTION && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                      onClick={async () => {
                        await handleRejectItem(item.id);
                      }}
                      disabled={isProcessing}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
    ], [editingItemIds, processingItems, itemDescriptions, itemFormStates]);

  // Filter and sort items
  const filteredItems = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();

    return items
      .filter(item => {
        // Apply type filter
        if (activeFilter === 'ai' && item.itemType !== TableItemSource.AI_SUGGESTION) return false;
        if (activeFilter === 'soa' && item.itemType !== TableItemSource.SOA_CONTROL) return false;

        // Apply search filter if query exists
        if (searchQuery) {
          const searchableText = [
            item.description,
            item.implementationDetails,
            item.rationale,
            item.id,
            ...(item.metadata?.source ? [item.metadata.source] : []),
            ...(item.metadata?.document ? [item.metadata.document] : [])
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();

          return searchableText.includes(searchLower);
        }

        return true;
      });
  }, [items, activeFilter, searchQuery]);

  // Apply pagination to filtered items
  const paginatedItems = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, currentPage, itemsPerPage]);

  // Process items to initialize descriptions and form states
  useEffect(() => {
    // Initialize item descriptions
    const descriptions: Record<string, string> = {};
    items.forEach(item => {
      descriptions[item.id] = item.description || '';
    });
    setItemDescriptions(descriptions);

    // Initialize item form states with default values
    const initialItemForms: Record<string, ItemFormState> = {};

    items.forEach(item => {
      if (item.itemType === TableItemSource.AI_SUGGESTION) {
        // Determine framework from iso_clauses if available for AI suggestions
        const framework = item.isoClauses ? getFrameworkFromIsoClauses(item.isoClauses) : '';

        initialItemForms[item.id] = {
          ...defaultFormState,
          framework
        };
      } else {
        // For SOA controls
        initialItemForms[item.id] = {
          ...defaultFormState,
          status: item.status || 'Planned'
        };
      }
    });

    setItemFormStates(initialItemForms);
  }, [items]);

  // This is handled directly by the DataTable component now

  // Handle global form state change
  const handleGlobalFormChange = (field: string, value: any) => {
    setGlobalFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply global values to selected items
  const applyGlobalValues = () => {
    const newItemFormStates = { ...itemFormStates };
    selectedItemIds.forEach(itemId => {
      // Get existing form state or create a new one with defaults
      const currentItemForm = itemFormStates[itemId] || { ...defaultFormState };

      // Apply global values
      newItemFormStates[itemId] = {
        ...currentItemForm,
        ...globalFormState
      };
    });
    setItemFormStates(newItemFormStates);

    // Show success message
    setStatusMessage({
      type: 'success',
      message: `Applied values to ${selectedItemIds.size} selected item${selectedItemIds.size !== 1 ? 's' : ''}`
    });

    // Clear message after delay
    setTimeout(() => {
      setStatusMessage(null);
    }, 3000);
  };

  return (
    <>
      {/* Card Header */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between">
          {/* Search Input */}
          <div className="relative flex-1 max-w-2xl">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-4 w-4 text-muted-foreground"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <Input
              type="text"
              placeholder="Search tasks..."
              className="pl-10 h-10 w-full bg-background"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filter controls */}
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
                  activeFilter === 'ai' 
                    ? 'bg-primary text-primary-foreground border-primary' 
                    : 'bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground'
                }`}
                onClick={() => setActiveFilter('ai')}
              >
                <div className="flex items-center gap-2">
                  <span>Tasks</span>
                  <span className={`inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-xs font-medium rounded-full ${activeFilter === 'ai' ? 'bg-card text-secondary' : 'bg-primary text-primary-foreground'}`}>
                    {items.filter(i => i.itemType === TableItemSource.AI_SUGGESTION).length}
                  </span>
                </div>
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border ${
                  activeFilter === 'soa' 
                    ? 'bg-primary text-primary-foreground border-primary' 
                    : 'bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground'
                }`}
                onClick={() => setActiveFilter('soa')}
              >
                <div className="flex items-center gap-2">
                  <span>Controls</span>
                  <span className={`inline-flex items-center justify-center h-5 min-w-5 px-1.5 text-xs font-medium rounded-full ${activeFilter === 'soa' ? 'bg-card text-secondary' : 'bg-primary text-primary-foreground'}`}>
                    {items.filter(i => i.itemType === TableItemSource.SOA_CONTROL).length}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* Search and filter status */}
      {/* <div className="px-4 py-2 border-b">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center text-sm text-muted-foreground">
            {searchQuery ? (
              <>
                <span>Showing {filteredItems.length} of {items.length} item{items.length !== 1 ? 's' : ''}</span>
                {filteredItems.length === 0 && items.length > 0 && (
                  <span className="ml-2 text-amber-600">No matching items found</span>
                )}
              </>
            ) : (
              <span>{items.length} total item{items.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {searchQuery && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1.5"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-3.5 w-3.5" />
                Clear search
              </Button>
            )}
            
            <div className="text-xs text-muted-foreground">
              Filtered by: <span className="font-medium text-foreground">
                {activeFilter === 'ai' ? 'AI Tasks' : 'SOA Controls'}
              </span>
            </div>
          </div>
        </div>
      </div> */}

      {/* Status message */}
      {statusMessage && (
        <div className="fixed bottom-4 right-4 z-50">
          <div 
            className={`flex items-center gap-3 rounded-lg border p-4 shadow-lg transition-all duration-300 ${
              statusMessage.type === 'success' 
                ? 'bg-green-50 text-green-800 border-green-200' 
                : statusMessage.type === 'error' 
                  ? 'bg-red-50 text-red-800 border-red-200' 
                  : 'bg-blue-50 text-blue-800 border-blue-200'
            }`}
            role="alert"
          >
            <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${
              statusMessage.type === 'success' 
                ? 'bg-green-100 text-green-600' 
                : statusMessage.type === 'error' 
                  ? 'bg-red-100 text-red-600' 
                  : 'bg-blue-100 text-blue-600'
            }`}>
              {statusMessage.type === 'success' && <Check className="h-5 w-5" />}
              {statusMessage.type === 'error' && <AlertCircle className="h-5 w-5" />}
              {statusMessage.type === 'info' && <FileText className="h-5 w-5" />}
            </div>
            <div className="text-sm font-medium">
              {statusMessage.message}
            </div>
            <button
              type="button"
              className="ml-4 -mx-1.5 -my-1.5 rounded-lg p-1.5 text-muted-foreground hover:text-foreground focus:ring-2 focus:ring-offset-2 focus:outline-none"
              onClick={() => setStatusMessage(null)}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedItemIds.size > 0 && (
        <div className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/95 shadow-sm">
          <div className="px-4 py-2.5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-primary/10 text-primary">
                <CheckSquare className="h-5 w-5" />
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {selectedItemIds.size} item{selectedItemIds.size !== 1 ? 's' : ''} selected
                </p>
                <p className="text-xs text-muted-foreground">
                  Bulk actions will apply to all selected items
                </p>
              </div>
            </div>

            <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                <div className="space-y-1">
                  <Label htmlFor="global-assignee" className="text-xs font-medium text-muted-foreground">
                    Assignee
                  </Label>
                  <div className="flex gap-2 items-start">
                    <div className="flex-1 min-w-0">
                      <SingleUserCombobox
                        selected={globalFormState.assignee}
                        setSelected={(value) => handleGlobalFormChange('assignee', value)}
                        placeholder="Select assignee"
                        className="w-full"
                      />
                    </div>
                    <Button
                      onClick={applyGlobalValues}
                      disabled={selectedItemIds.size === 0 || !globalFormState.assignee}
                      className="h-9 whitespace-nowrap shrink-0"
                      variant="outline"
                      size="sm"
                    >
                      <UserPlus className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Apply</span>
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground block">
                    Quick Actions
                  </Label>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-9 flex-1 border-destructive/20 text-destructive hover:bg-destructive/5 hover:text-destructive"
                      onClick={() => {
                        const selectedItems = items.filter(item => selectedItemIds.has(item.id));
                        const processQueue = async () => {
                          setStatusMessage({ type: 'info', message: `Processing ${selectedItems.length} items...` });
                          let successCount = 0;
                          let errorCount = 0;

                          for (const item of selectedItems) {
                            try {
                              await handleRejectItem(item.id);
                              successCount++;
                            } catch (error) {
                              console.error(`Error rejecting item ${item.id}:`, error);
                              errorCount++;
                            }
                          }

                          setStatusMessage({
                            type: errorCount > 0 ? 'error' : 'success',
                            message: `Rejected ${successCount} item${successCount !== 1 ? 's' : ''} successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}.`
                          });
                          setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s

                          setSelectedItemIds(new Set());
                          if (onUpdate && successCount > 0) onUpdate();
                        };

                        processQueue();
                      }}
                    >
                      <X className="mr-2 h-4 w-4" />
                      Reject Selected
                    </Button>
                    
                    <Button
                      onClick={() => {
                        const selectedItems = items
                        .filter(item => selectedItemIds.has(item.id) && item.itemType === TableItemSource.AI_SUGGESTION);
    
                      if (selectedItems.length === 0) {
                        setStatusMessage({ type: 'info', message: 'No AI suggestions selected. Only AI suggestions can be rejected.' });
                        setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s
                        return;
                      }
    
                        const processQueue = async () => {
                          setStatusMessage({ type: 'info', message: `Processing ${selectedItems.length} items...` });
                          let successCount = 0;
                          let errorCount = 0;

                          for (const item of selectedItems) {
                            try {
                              await handleAcceptItem(item);
                              successCount++;
                            } catch (error) {
                              console.error(`Error accepting item ${item.id}:`, error);
                              errorCount++;
                            }
                          }

                          setStatusMessage({
                            type: errorCount > 0 ? 'error' : 'success',
                            message: `Accepted ${successCount} item${successCount !== 1 ? 's' : ''} successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}.`
                          });
                          setTimeout(() => setStatusMessage(null), 3000); // DED-1898: Auto-dismiss after 3s

                          setSelectedItemIds(new Set());
                          if (onUpdate && successCount > 0) onUpdate();
                        };

                        processQueue();
                      }}
                      disabled={selectedItemIds.size === 0}
                      className="h-9 bg-primary hover:bg-primary/80 text-white flex-1"
                    >
                      <Check className="mr-2 h-4 w-4" />
                      Accept Selected
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="p-0">
        <Table className="w-full min-w-[700px] max-w-full rounded-lg table">
          <TableHeader>
            <TableRow>
              {/* Custom checkbox column */}
              <TableHead className="w-[50px] pl-4 pr-0 py-2">
                <Checkbox
                  checked={filteredItems.length > 0 && selectedItemIds.size === filteredItems.length}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      // Select all items
                      setSelectedItemIds(new Set(filteredItems.map(item => item.id)));
                    } else {
                      // Deselect all items
                      setSelectedItemIds(new Set());
                    }
                  }}
                />
              </TableHead>

              {/* Regular column headers */}
              {columns.map(column => (
                <TableHead
                  key={column.id}
                  className={`py-2 text-xs font-semibold uppercase bg-muted/60 text-muted-foreground h-10 whitespace-nowrap text-ellipsis ${(column.meta as any)?.headerClassName || ''}`}
                >
                  {typeof column.header === 'string' ? column.header : ''}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {paginatedItems.map(item => (
              <TableRow key={item.id} className={selectedItemIds.has(item.id) ? 'bg-muted/50' : ''}>
                {/* Checkbox cell */}
                <TableCell className="w-[50px] pl-4 pr-0 py-2">
                  <Checkbox
                    checked={selectedItemIds.has(item.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedItemIds(prev => new Set([...prev, item.id]));
                      } else {
                        setSelectedItemIds(prev => {
                          const newSet = new Set(prev);
                          newSet.delete(item.id);
                          return newSet;
                        });
                      }
                    }}
                  />
                </TableCell>

                {/* Description cell */}
                <TableCell className={`pl-0 pr-4 min-w-[400px] md:min-w-[500px] whitespace-normal align-top`}>
                  <div className="space-y-1">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div>
                          {editingItemIds.has(item.id) ? (
                            <EditDescriptionField
                              item={item}
                              editingDescription={itemDescriptions[item.id] || item.description || ''}
                              setEditingDescription={(value) => handleDescriptionChange(item.id, value)}
                              handleSave={() => toggleEditItem(item.id)}
                              handleCancel={() => toggleEditItem(item.id)}
                            />
                          ) : (
                            <div className="flex items-center gap-2">
                              <div
                                className="font-medium cursor-pointer hover:bg-muted/50 p-2 rounded transition-colors group relative flex-1"
                                onClick={() => toggleEditItem(item.id)}
                                title={itemDescriptions[item.id] || item.description || '(No description)'}
                              >
                                {itemDescriptions[item.id] || item.description || '(No description)'}
                              </div>
                              {item.itemType === TableItemSource.AI_SUGGESTION && (
                                <span 
                                  className="h-3 w-3 text-gray-400 flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors" 
                                  title="Click to edit task"
                                  onClick={() => toggleEditItem(item.id)}
                                >
                                  <i className="fa-solid fa-pen-to-square"></i>
                                </span>
                              )}
                            </div>
                          )}
                          {/* {item.rationale && (
                            <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                              {item.rationale}
                            </div>
                          )} */}
                          {item.implementationDetails && (
                            <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
                              {item.implementationDetails}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Badges section */}
                    <div className="mt-1 flex flex-wrap gap-1 items-center">
                      {item.itemType === TableItemSource.AI_SUGGESTION && item.confidence !== undefined && (
                        <Badge variant="outline" className="text-xs font-normal gap-1 h-5">
                          <span className={`inline-block w-2 h-2 rounded-full ${(item.confidence > 0.8 || item.confidence > 80) ? 'bg-green-500' :
                            (item.confidence > 0.5 || item.confidence > 50) ? 'bg-yellow-500' : 'bg-red-500'
                            }`} />
                          {formatConfidence(item.confidence)}
                        </Badge>
                      )}
                      {item.itemType === TableItemSource.AI_SUGGESTION && item.isoClauses && item.isoClauses.length > 0 && (
                        <>
                          {/* Compact framework/control pills */}
                          {formatIsoClausesAsPills(item.isoClauses).map((pill, i) => (
                            <Badge key={i} variant="outline" className="text-xs font-normal h-5">
                              {pill}
                            </Badge>
                          ))}
                        </>
                      )}
                      {item.itemType === TableItemSource.SOA_CONTROL && item.status && (
                        <Badge variant="outline" className="text-xs font-normal h-5">
                          Status: {item.status}
                        </Badge>
                      )}
                      {item.itemType === TableItemSource.SOA_CONTROL && item.controlNumber && (
                        <Badge variant="outline" className="text-xs font-normal h-5">
                          Control: {item.controlNumber}
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>

                {/* Source cell */}
                <TableCell className="w-[180px] max-w-[180px] truncate">
                  <div className="text-sm line-clamp-3">
                    {item.itemType === TableItemSource.AI_SUGGESTION ?
                      (item.documentName || '—') :
                      `Control ${item.controlNumber || item.originalId}`
                    }
                  </div>
                </TableCell>

                {/* Assignee cell */}
                <TableCell className="w-[180px]">
                  <div className="w-full">
                    <SingleUserCombobox
                      selected={itemFormStates[item.id]?.assignee || ''}
                      setSelected={(value) => handleItemFormChange(item.id, 'assignee', value)}
                      placeholder="Select assignee"
                      className="w-full"
                    />
                  </div>
                </TableCell>

                {/* Actions cell */}
                <TableCell className="w-[180px]">
                  <div className="flex items-center gap-2 justify-end">
                    {editingItemIds.has(item.id) ? (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                          onClick={() => handleSaveItem(item)}
                          disabled={processingItems.has(item.id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                          onClick={() => toggleEditItem(item.id)}
                          disabled={processingItems.has(item.id)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-green-600 hover:bg-green-50"
                          onClick={() => handleAcceptItem(item)}
                          disabled={processingItems.has(item.id)}
                        >
                          {processingItems.has(item.id) ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )}
                        </Button>
                        {/* Only show reject button for AI suggestions */}
                        {item.itemType === TableItemSource.AI_SUGGESTION && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                            onClick={async () => {
                              await handleRejectItem(item.id);
                            }}
                            disabled={processingItems.has(item.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-center">
            {/* Create a fake tanstack/react-table compatible object for DataTablePagination */}
            <DataTablePagination
              table={{
                getState: () => ({
                  pagination: {
                    pageIndex: currentPage,
                    pageSize: itemsPerPage,
                  },
                }),
                getPageCount: () => Math.ceil(filteredItems.length / itemsPerPage),
                getCanPreviousPage: () => currentPage > 0,
                getCanNextPage: () => currentPage < Math.ceil(filteredItems.length / itemsPerPage) - 1,
                previousPage: () => setCurrentPage(prev => Math.max(0, prev - 1)),
                nextPage: () => setCurrentPage(prev => Math.min(Math.ceil(filteredItems.length / itemsPerPage) - 1, prev + 1)),
                setPageIndex: (index: number) => setCurrentPage(index),
                setPageSize: (size: number) => {
                  setCurrentPage(0); // Reset to first page when changing page size
                  setItemsPerPage(size);
                },
              } as any}
              pageSizeOptions={[5, 10, 20, 50, 100]}
            />
          </div>
        </div>
      </div>
    </>
  );
};

// EditDescriptionField component for editing descriptions
interface EditDescriptionFieldProps {
  item: NormalizedTableItem;
  editingDescription: string;
  setEditingDescription: (value: string) => void;
  handleSave: () => void;
  handleCancel: () => void;
}

const EditDescriptionField: React.FC<EditDescriptionFieldProps> = ({
  item,
  editingDescription,
  setEditingDescription,
  handleSave,
  handleCancel
}) => {
  // Calculate appropriate rows based on content length
  const calculateRows = (text: string): number => {
    if (!text) return 3;
    const lineCount = text.split('\n').length;
    const estimatedRows = Math.ceil(text.length / 80); // Rough estimate of characters per line
    return Math.max(3, Math.min(lineCount, estimatedRows, 8)); // Between 3 and 8 rows
  };

  const [rows, setRows] = useState(calculateRows(editingDescription));

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setEditingDescription(value);
    setRows(calculateRows(value));
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={editingDescription}
        onChange={handleChange}
        rows={rows}
        className="w-full min-h-[80px] text-sm resize-none"
        placeholder="Enter task description..."
        style={{
          minHeight: `${Math.max(80, rows * 24)}px`, // 24px per line minimum
        }}
      />
      <div className="flex items-center gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          className="h-8 px-2"
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={handleSave}
          className="h-8 px-2 bg-green-600 hover:bg-green-700"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

// Filter badge component for filter pills
interface FilterBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  children: React.ReactNode;
}

const FilterBadge = ({ active, children, ...props }: FilterBadgeProps) => (
  <div
    className={`px-3 py-1 mr-2 text-sm rounded-md cursor-pointer transition-colors ${active
      ? 'bg-primary text-foreground'
      : 'bg-transparent border border-border text-gray-600 hover:bg-primary/10'}`}
    {...props}
  >
    {children}
  </div>
);

export default UnifiedTableCell;
